import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import {
  Factory,
  Globe,
  Ship,
  Users,
  Star,
  Mail,
  Phone,
  Linkedin,
  ExternalLink,
  Edit2,
  Save,
  X,
  Plus,
  ChevronLeft,
  Clock,
  MessageSquare,
  Send,
  User,
  Calendar,
  Target,
  Trash2,
  MoreVertical,
  BarChart3,
  TrendingUp,
  Package
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const PIPELINE_STAGES = [
  { value: "raw_imported", label: "Raw Imported" },
  { value: "profile_enriched", label: "Profile Enriched" },
  { value: "contact_identified", label: "Contact Identified" },
  { value: "outreach_sent", label: "Outreach Sent" },
  { value: "response_received", label: "Response Received" },
  { value: "requirement_identified", label: "Requirement Identified" },
  { value: "qualified_lead", label: "Qualified Lead" },
  { value: "converted", label: "Converted" },
  { value: "lost", label: "Lost" }
];

const ExportFactoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [factory, setFactory] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Dialogs
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showDeleteContactDialog, setShowDeleteContactDialog] = useState(false);
  const [newContact, setNewContact] = useState({});
  const [editingContact, setEditingContact] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [newActivity, setNewActivity] = useState({ activity_type: 'note', summary: '' });
  const [emailData, setEmailData] = useState({ contact_id: '', template_id: '', subject: '', body: '' });
  
  // Shipment analytics
  const [shipmentStats, setShipmentStats] = useState(null);

  useEffect(() => {
    fetchFactory();
    fetchContacts();
    fetchActivities();
    fetchShipments();
    fetchTemplates();
    fetchTopProducts();
  }, [id]);

  const fetchFactory = async () => {
    try {
      const response = await axios.get(`${API}/export/factories/${id}`);
      setFactory(response.data);
      setEditData(response.data);
      // If factory has top_products cached, use them
      if (response.data.top_products) {
        setTopProducts(response.data.top_products);
      }
    } catch (error) {
      toast.error("Failed to load factory");
      navigate('/export/factories');
    } finally {
      setLoading(false);
    }
  };

  const fetchTopProducts = async () => {
    try {
      const response = await axios.get(`${API}/export/factories/${id}/top-products`);
      setTopProducts(response.data.top_products || []);
    } catch (error) {
      console.error("Failed to load top products");
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/export/factories/${id}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error("Failed to load contacts");
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/export/factories/${id}/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error("Failed to load activities");
    }
  };

  const fetchShipments = async () => {
    try {
      const response = await axios.get(`${API}/export/factories/${id}/shipments`);
      setShipments(response.data);
      
      // Calculate analytics from shipment data
      if (response.data.length > 0) {
        const stats = calculateShipmentStats(response.data);
        setShipmentStats(stats);
      }
    } catch (error) {
      console.error("Failed to load shipments");
    }
  };

  const calculateShipmentStats = (data) => {
    // Group by shipper
    const shipperCounts = {};
    data.forEach(s => {
      const shipper = s.shipper_name || 'Unknown';
      shipperCounts[shipper] = (shipperCounts[shipper] || 0) + 1;
    });
    const topShippers = Object.entries(shipperCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name: name.substring(0, 20), count }));

    // Group by HS code
    const hsCodeCounts = {};
    data.forEach(s => {
      const code = s.hs_code || 'N/A';
      hsCodeCounts[code] = (hsCodeCounts[code] || 0) + 1;
    });
    const topHsCodes = Object.entries(hsCodeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([code, count]) => ({ code, count }));

    // Total quantity by unit
    const quantityByUnit = {};
    data.forEach(s => {
      if (s.quantity && s.unit) {
        quantityByUnit[s.unit] = (quantityByUnit[s.unit] || 0) + s.quantity;
      }
    });

    return {
      totalShipments: data.length,
      uniqueShippers: Object.keys(shipperCounts).length,
      topShippers,
      topHsCodes,
      quantityByUnit
    };
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/export/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to load templates");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.patch(`${API}/export/factories/${id}`, editData);
      setFactory(response.data);
      setIsEditing(false);
      toast.success("Factory updated");
    } catch (error) {
      toast.error("Failed to update factory");
    } finally {
      setSaving(false);
    }
  };

  const handlePipelineChange = async (stage) => {
    try {
      await axios.patch(`${API}/export/factories/${id}/pipeline`, null, {
        params: { stage }
      });
      setFactory({ ...factory, pipeline_stage: stage });
      fetchActivities();
      toast.success("Pipeline stage updated");
    } catch (error) {
      toast.error("Failed to update pipeline");
    }
  };

  const handleAddContact = async () => {
    try {
      await axios.post(`${API}/export/factories/${id}/contacts`, newContact);
      toast.success("Contact added");
      setShowContactDialog(false);
      setNewContact({});
      fetchContacts();
      fetchFactory();
    } catch (error) {
      toast.error("Failed to add contact");
    }
  };

  const handleEditContact = async () => {
    if (!editingContact) return;
    try {
      await axios.patch(`${API}/export/contacts/${editingContact.id}`, newContact);
      toast.success("Contact updated");
      setShowContactDialog(false);
      setNewContact({});
      setEditingContact(null);
      fetchContacts();
    } catch (error) {
      toast.error("Failed to update contact");
    }
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete) return;
    try {
      await axios.delete(`${API}/export/contacts/${contactToDelete.id}`);
      toast.success("Contact deleted");
      setShowDeleteContactDialog(false);
      setContactToDelete(null);
      fetchContacts();
      fetchFactory();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const openEditContactDialog = (contact) => {
    setEditingContact(contact);
    setNewContact({
      name: contact.name,
      title: contact.title,
      email: contact.email,
      phone: contact.phone,
      department: contact.department,
      linkedin_url: contact.linkedin_url,
      is_primary: contact.is_primary
    });
    setShowContactDialog(true);
  };

  const openAddContactDialog = () => {
    setEditingContact(null);
    setNewContact({});
    setShowContactDialog(true);
  };

  const handleAddActivity = async () => {
    try {
      await axios.post(`${API}/export/factories/${id}/activities`, newActivity);
      toast.success("Activity logged");
      setShowActivityDialog(false);
      setNewActivity({ activity_type: 'note', summary: '' });
      fetchActivities();
    } catch (error) {
      toast.error("Failed to log activity");
    }
  };

  const handleSendEmail = async () => {
    if (!emailData.contact_id) {
      toast.error("Please select a contact");
      return;
    }
    try {
      await axios.post(`${API}/export/send-email`, {
        factory_id: id,
        ...emailData
      });
      toast.success("Email logged (Mock - not actually sent in V1)");
      setShowEmailDialog(false);
      setEmailData({ contact_id: '', template_id: '', subject: '', body: '' });
      fetchActivities();
      fetchFactory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setEmailData({
        ...emailData,
        template_id: templateId,
        subject: template.subject,
        body: template.body
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="export-factory-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/export/factories')} className="p-2">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">{factory?.name}</h1>
            <Badge className="bg-yellow-100 text-yellow-800 gap-1">
              <Star className="w-3 h-3" />
              Score: {factory?.target_score}
            </Badge>
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <Globe className="w-3 h-3" />
              {factory?.country}
            </span>
            <span className="flex items-center gap-1">
              <Ship className="w-3 h-3" />
              {factory?.shipment_count} shipments
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {factory?.supplier_count} suppliers
            </span>
            {factory?.hs_codes?.length > 0 && (
              <span className="flex items-center gap-1">
                <Package className="w-3 h-3" />
                {factory.hs_codes.length} HSN codes
              </span>
            )}
            {factory?.import_countries?.length > 0 && (
              <span className="flex items-center gap-1 text-blue-600">
                <Globe className="w-3 h-3" />
                Imports from: {factory.import_countries.slice(0, 3).join(', ')}{factory.import_countries.length > 3 ? '...' : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={factory?.pipeline_stage} onValueChange={handlePipelineChange}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PIPELINE_STAGES.map((stage) => (
                <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                <X className="w-4 h-4" />
              </Button>
              <Button onClick={handleSave} disabled={saving} className="gap-2 bg-blue-600">
                <Save className="w-4 h-4" />
                Save
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => setIsEditing(true)} className="gap-2">
              <Edit2 className="w-4 h-4" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="shipments">Shipments ({factory?.shipment_count || 0})</TabsTrigger>
          <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
          <TabsTrigger value="communication">Communication ({activities.length})</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Factory Info */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Factory Information</CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={editData.city || ''}
                        onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Website</Label>
                      <Input
                        value={editData.website || ''}
                        onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={editData.boardline_phone || ''}
                        onChange={(e) => setEditData({ ...editData, boardline_phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>LinkedIn</Label>
                      <Input
                        value={editData.linkedin_url || ''}
                        onChange={(e) => setEditData({ ...editData, linkedin_url: e.target.value })}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={editData.notes || ''}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        rows={3}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">City</p>
                      <p className="font-medium">{factory?.city || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Website</p>
                      {factory?.website ? (
                        <a href={factory.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          {factory.website} <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : '-'}
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <p className="font-medium">{factory?.boardline_phone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">LinkedIn</p>
                      {factory?.linkedin_url ? (
                        <a href={factory.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          View Profile <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : '-'}
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-slate-500">Notes</p>
                      <p className="font-medium">{factory?.notes || '-'}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions & Stats */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button className="w-full gap-2" onClick={() => setShowEmailDialog(true)}>
                    <Send className="w-4 h-4" />
                    Send Email
                  </Button>
                  <Button variant="outline" className="w-full gap-2" onClick={() => setShowContactDialog(true)}>
                    <Plus className="w-4 h-4" />
                    Add Contact
                  </Button>
                  <Button variant="outline" className="w-full gap-2" onClick={() => setShowActivityDialog(true)}>
                    <MessageSquare className="w-4 h-4" />
                    Log Activity
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Top Suppliers</CardTitle>
                </CardHeader>
                <CardContent>
                  {factory?.top_suppliers?.length > 0 ? (
                    <ul className="space-y-2">
                      {factory.top_suppliers.map((supplier, idx) => (
                        <li key={idx} className="text-sm text-slate-600 flex items-center gap-2">
                          <span className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center text-xs">{idx + 1}</span>
                          {supplier}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-400">No supplier data</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Products Card */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-4 h-4 text-green-600" />
                    Top 10 Products
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topProducts.length > 0 ? (
                    <ul className="space-y-2 max-h-64 overflow-y-auto">
                      {topProducts.map((product, idx) => (
                        <li key={idx} className="text-sm border-b border-slate-100 pb-2 last:border-0">
                          <div className="flex items-start gap-2">
                            <span className="w-5 h-5 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                              {idx + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-slate-700 leading-tight line-clamp-2">{product.product}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {product.shipment_count} shipments
                                {product.total_qty > 0 && ` • ${product.total_qty.toLocaleString()} ${product.unit || 'units'}`}
                              </p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-400">No product data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Shipments Tab - Shipment Intelligence */}
        <TabsContent value="shipments">
          <div className="space-y-6">
            {/* Stats Cards */}
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total Shipments</p>
                      <p className="text-2xl font-bold">{factory?.shipment_count || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Users className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Unique Suppliers</p>
                      <p className="text-2xl font-bold">{shipmentStats?.uniqueShippers || factory?.supplier_count || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {shipmentStats && Object.entries(shipmentStats.quantityByUnit || {}).slice(0, 2).map(([unit, qty]) => (
                <Card key={unit}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <TrendingUp className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total ({unit})</p>
                        <p className="text-2xl font-bold">{qty.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Charts Row */}
            {shipmentStats && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Suppliers Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Top Suppliers
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {shipmentStats.topShippers.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={shipmentStats.topShippers} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-center text-slate-400 py-8">No data</p>
                    )}
                  </CardContent>
                </Card>

                {/* HS Codes Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Top HS Codes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {shipmentStats.topHsCodes.length > 0 ? (
                      <div className="space-y-3">
                        {shipmentStats.topHsCodes.map((item, idx) => (
                          <div key={item.code} className="flex items-center gap-3">
                            <span className="text-sm font-mono text-slate-600 w-20">{item.code}</span>
                            <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                              <div 
                                className="h-full bg-green-500 rounded-full"
                                style={{ width: `${(item.count / shipmentStats.topHsCodes[0].count) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-12 text-right">{item.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-slate-400 py-8">No data</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Top 10 Products Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="w-4 h-4 text-green-600" />
                  Top 10 Products Imported
                  <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200">
                    Based on Product Description
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topProducts.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Product Description</TableHead>
                        <TableHead className="text-right">Shipments</TableHead>
                        <TableHead className="text-right">Total Quantity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topProducts.map((product, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium max-w-md">
                            <p className="line-clamp-2">{product.product}</p>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary">{product.shipment_count}</Badge>
                          </TableCell>
                          <TableCell className="text-right text-slate-600">
                            {product.total_qty > 0 ? (
                              <span>{product.total_qty.toLocaleString()} {product.unit || ''}</span>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No product data available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Shipments Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  Shipment Records
                  {factory?.shipment_count > 100 && (
                    <Badge variant="outline" className="text-slate-500">
                      Showing latest 100 of {factory.shipment_count.toLocaleString()}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {shipments.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">No shipment data available</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Shipper</TableHead>
                        <TableHead>HS Code</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Ports</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shipments.slice(0, 50).map((shipment) => (
                        <TableRow key={shipment.id}>
                          <TableCell className="font-medium">{shipment.shipper_name || '-'}</TableCell>
                          <TableCell>{shipment.hs_code || '-'}</TableCell>
                          <TableCell className="max-w-xs truncate">{shipment.product_description || '-'}</TableCell>
                          <TableCell>{shipment.quantity} {shipment.unit}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {shipment.origin_port} → {shipment.destination_port}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Contacts</CardTitle>
              <Button size="sm" className="gap-1" onClick={openAddContactDialog}>
                <Plus className="w-4 h-4" />
                Add Contact
              </Button>
            </CardHeader>
            <CardContent>
              {contacts.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p>No contacts yet. Add a contact to start outreach.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="p-4 border border-slate-200 rounded-lg hover:shadow-sm transition-shadow" data-testid={`contact-card-${contact.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{contact.name}</p>
                          <p className="text-sm text-slate-500">{contact.title || 'No title'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {contact.is_primary && (
                            <Badge variant="secondary">Primary</Badge>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditContactDialog(contact)}>
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => { setContactToDelete(contact); setShowDeleteContactDialog(true); }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1 text-sm">
                        {contact.email && (
                          <p className="flex items-center gap-2 text-slate-600">
                            <Mail className="w-3 h-3" />
                            <a href={`mailto:${contact.email}`} className="hover:text-blue-600">{contact.email}</a>
                          </p>
                        )}
                        {contact.phone && (
                          <p className="flex items-center gap-2 text-slate-600">
                            <Phone className="w-3 h-3" />
                            {contact.phone}
                          </p>
                        )}
                        {contact.department && (
                          <p className="flex items-center gap-2 text-slate-500 capitalize">
                            <User className="w-3 h-3" />
                            {contact.department.replace('_', ' ')}
                          </p>
                        )}
                        {contact.linkedin_url && (
                          <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:underline">
                            <Linkedin className="w-3 h-3" />
                            LinkedIn Profile
                          </a>
                        )}
                      </div>
                      {contact.email && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-3 w-full gap-1"
                          onClick={() => {
                            setEmailData({ ...emailData, contact_id: contact.id });
                            setShowEmailDialog(true);
                          }}
                        >
                          <Send className="w-3 h-3" />
                          Send Email
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Communication Tab */}
        <TabsContent value="communication">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Activity Timeline</CardTitle>
              <Button size="sm" className="gap-1" onClick={() => setShowActivityDialog(true)}>
                <Plus className="w-4 h-4" />
                Log Activity
              </Button>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p>No activities logged yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activities.map((activity) => (
                    <div key={activity.id} className="flex gap-4 p-4 bg-slate-50 rounded-lg">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${
                        activity.activity_type === 'email' ? 'bg-blue-500' :
                        activity.activity_type === 'call' ? 'bg-green-500' :
                        activity.activity_type === 'meeting' ? 'bg-purple-500' : 'bg-slate-400'
                      }`}>
                        {activity.activity_type === 'email' ? <Mail className="w-5 h-5" /> :
                         activity.activity_type === 'call' ? <Phone className="w-5 h-5" /> :
                         <MessageSquare className="w-5 h-5" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-slate-900 capitalize">{activity.activity_type}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(activity.created_at).toLocaleString()}
                          </p>
                        </div>
                        {activity.subject && (
                          <p className="text-sm font-medium text-slate-700">{activity.subject}</p>
                        )}
                        <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{activity.summary}</p>
                        <p className="text-xs text-slate-400 mt-2">by {activity.created_by_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContact ? "Edit Contact" : "Add Contact"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={newContact.name || ''} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} data-testid="contact-name-input" />
              </div>
              <div>
                <Label>Title</Label>
                <Input value={newContact.title || ''} onChange={(e) => setNewContact({ ...newContact, title: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={newContact.email || ''} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} data-testid="contact-email-input" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={newContact.phone || ''} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} />
              </div>
              <div>
                <Label>Department</Label>
                <Select value={newContact.department || ''} onValueChange={(v) => setNewContact({ ...newContact, department: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="merchandising">Merchandising</SelectItem>
                    <SelectItem value="sourcing">Sourcing</SelectItem>
                    <SelectItem value="product_development">Product Development</SelectItem>
                    <SelectItem value="management">Management</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>LinkedIn URL</Label>
                <Input value={newContact.linkedin_url || ''} onChange={(e) => setNewContact({ ...newContact, linkedin_url: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="is_primary"
                checked={newContact.is_primary || false}
                onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_primary" className="text-sm font-normal cursor-pointer">Mark as primary contact</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowContactDialog(false); setEditingContact(null); setNewContact({}); }}>Cancel</Button>
            <Button onClick={editingContact ? handleEditContact : handleAddContact} disabled={!newContact.name} data-testid="contact-save-btn">
              {editingContact ? "Update" : "Add"} Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Contact Confirmation */}
      <AlertDialog open={showDeleteContactDialog} onOpenChange={setShowDeleteContactDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{contactToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteContact} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Log Activity Dialog */}
      <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Activity</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Activity Type</Label>
              <Select value={newActivity.activity_type} onValueChange={(v) => setNewActivity({ ...newActivity, activity_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">Note</SelectItem>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={newActivity.subject || ''} onChange={(e) => setNewActivity({ ...newActivity, subject: e.target.value })} />
            </div>
            <div>
              <Label>Summary *</Label>
              <Textarea value={newActivity.summary} onChange={(e) => setNewActivity({ ...newActivity, summary: e.target.value })} rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivityDialog(false)}>Cancel</Button>
            <Button onClick={handleAddActivity} disabled={!newActivity.summary}>Log Activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send Outreach Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>To Contact *</Label>
                <Select value={emailData.contact_id} onValueChange={(v) => setEmailData({ ...emailData, contact_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.filter(c => c.email).map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name} ({contact.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Use Template</Label>
                <Select value={emailData.template_id || "none"} onValueChange={(v) => handleTemplateSelect(v === "none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select template..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No template</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={emailData.subject} onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })} />
            </div>
            <div>
              <Label>Body</Label>
              <Textarea value={emailData.body} onChange={(e) => setEmailData({ ...emailData, body: e.target.value })} rows={8} />
              <p className="text-xs text-slate-500 mt-1">
                Available variables: {'{{factory_name}}'}, {'{{contact_name}}'}, {'{{contact_title}}'}, {'{{country}}'}
              </p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>V1 Note:</strong> Emails are logged but not actually sent. Real email sending will be enabled in V2.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={!emailData.contact_id} className="gap-2">
              <Send className="w-4 h-4" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExportFactoryDetail;
